import { FC, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Inbox } from 'lucide-react';

import { useGetSimulationRunYardMoves } from '@/api/generated';
import {
  ChartExportButton,
  ChartExportHeader,
} from '@/components/charts/chart-export-button';
import type { ChartExportFilter } from '@/components/charts/types';
import { useChartExport } from '@/components/charts/use-chart-export';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { SimulationYardMovesFilterBar } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-yard-moves-filter-bar';
import { SimulationYardMovesPanel } from '@/routes/(app)/(scenarios)/(simulation)/components/simulation-yard-moves-panel';
import { useYardMovesChart } from '@/routes/(app)/(scenarios)/(simulation)/hooks/use-yard-moves-chart';
import {
  DEFAULT_LOCALE,
  OTHER_VISITS_KEY,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves.utils';
import {
  DEFAULT_YARD_MOVES_FILTERS,
  type YardMovesChartFilters,
} from '@/routes/(app)/(scenarios)/(simulation)/yard-moves-chart.utils';
import { isNotFoundError } from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

const PANEL_LIST_FORMAT = new Intl.ListFormat(DEFAULT_LOCALE, {
  type: 'conjunction',
});

interface SimulationYardMovesChartProps {
  simulationId: string;
}

/** Yard IN/OUT moves per shift, one stacked bar panel per move category. */
export const SimulationYardMovesChart: FC<SimulationYardMovesChartProps> = ({
  simulationId,
}) => {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useGetSimulationRunYardMoves(
    simulationId,
    { query: { enabled: !!simulationId } }
  );

  const [filters, setFilters] = useState(DEFAULT_YARD_MOVES_FILTERS);
  // Shared by all panels so hovering one highlights the shift in each.
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  // The filter bar binds to `filters` so a click ticks instantly; the chart
  // (hundreds of bars) re-renders in a deferred, interruptible pass.
  const deferredFilters = useDeferredValue(filters);
  const patchFilters = useCallback((patch: Partial<YardMovesChartFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);
  const {
    timezone,
    visiblePanels,
    hasVisibleMoves,
    dayTicks,
    shiftEndById,
    vesselNameById,
    visitOptions,
    exportFilters,
  } = useYardMovesChart(data, deferredFilters);
  const exportFilterLabels = useMemo<Array<ChartExportFilter>>(
    () => exportFilters.map(({ key, active }) => ({ label: t(key), active })),
    [exportFilters, t]
  );
  const exportTabLabel = useMemo(
    () =>
      PANEL_LIST_FORMAT.format(
        visiblePanels.map((panel) =>
          t(`simulation.charts.yardMoves.panels.${panel.category}`)
        )
      ),
    [visiblePanels, t]
  );
  const { captureRef, exportChart, isExporting } = useChartExport({
    title: t('simulation.charts.yardMoves.title'),
    activeTabLabel: exportTabLabel,
    exportFilters: exportFilterLabels,
  });
  // Memoised elements: a hover (which re-renders this component) must not
  // re-render the export header (it formats `new Date()`) or button tooltip.
  const exportHeader = useMemo(
    () => (
      <ChartExportHeader
        title={t('simulation.charts.yardMoves.title')}
        activeTabLabel={exportTabLabel}
        exportFilters={exportFilterLabels}
      />
    ),
    [t, exportTabLabel, exportFilterLabels]
  );
  const exportButton = useMemo(
    () => (
      <ChartExportButton
        onClick={exportChart}
        isExporting={isExporting}
      />
    ),
    [exportChart, isExporting]
  );

  const toggleVisit = useCallback((visitId: string) => {
    if (visitId === OTHER_VISITS_KEY) return;
    setFilters((current) => ({
      ...current,
      selectedVisitIds: current.selectedVisitIds.includes(visitId)
        ? current.selectedVisitIds.filter((id) => id !== visitId)
        : [...current.selectedVisitIds, visitId],
    }));
  }, []);

  const renderEmpty = (title: string) => (
    <Empty className='border border-dashed'>
      <EmptyHeader>
        <EmptyMedia variant='icon'>
          <Inbox />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
      </EmptyHeader>
    </Empty>
  );

  const renderPanels = () => (
    <div
      ref={captureRef}
      data-yard-moves-panels
      className='bg-background flex flex-col gap-4'>
      <div
        data-chart-export-header
        className='hidden'>
        {exportHeader}
      </div>
      {visiblePanels.map((panel) => (
        <SimulationYardMovesPanel
          key={panel.category}
          title={t(`simulation.charts.yardMoves.panels.${panel.category}`)}
          panel={panel}
          timezone={timezone}
          dayTicks={dayTicks}
          shiftEndById={shiftEndById}
          vesselNameById={vesselNameById}
          onToggleVisit={toggleVisit}
          hoveredRowIndex={hoveredRowIndex}
          onHoverRow={setHoveredRowIndex}
        />
      ))}
    </div>
  );

  const renderBody = () => {
    if (isLoading) {
      return <Skeleton className='h-[560px] w-full' />;
    }
    if (error) {
      const notFound = isNotFoundError(error);
      return (
        <Empty className='border border-dashed'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <AlertCircle />
            </EmptyMedia>
            <EmptyTitle>
              {t(
                notFound
                  ? 'simulation.charts.yardMoves.notFound'
                  : 'simulation.charts.yardMoves.error'
              )}
            </EmptyTitle>
            <EmptyDescription>
              {t(
                notFound
                  ? 'simulation.charts.yardMoves.notFoundDescription'
                  : 'simulation.charts.yardMoves.errorDescription'
              )}
            </EmptyDescription>
          </EmptyHeader>
          {!notFound && (
            <Button
              variant='outline'
              size='sm'
              onClick={async () => {
                await refetch();
              }}>
              {t('general.action.tryAgain')}
            </Button>
          )}
        </Empty>
      );
    }
    if (!data?.movements.length) {
      return renderEmpty(t('simulation.charts.yardMoves.empty'));
    }
    return (
      <div className='flex flex-col gap-4'>
        <SimulationYardMovesFilterBar
          filters={filters}
          onChange={patchFilters}
          visitOptions={visitOptions}
        />
        {hasVisibleMoves
          ? renderPanels()
          : renderEmpty(t('simulation.charts.yardMoves.noMatch'))}
      </div>
    );
  };

  return (
    <Card className='group relative col-span-full'>
      {hasVisibleMoves && !isLoading && !error && exportButton}
      <CardHeader className='py-4'>
        <CardTitle className='text-base'>
          {t('simulation.charts.yardMoves.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>{renderBody()}</CardContent>
    </Card>
  );
};
